import { SettingsCard } from './SettingsCard';
import { SettingsToggle } from './SettingsToggle';
import { settingRowStyle, settingLabelColumnStyle, settingLabelStyle, settingsButtonStyle, settingsDescriptionStyle, settingsPrimaryButtonStyle } from './settingsStyles';
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import {
  v2,
  type NotificationSubscriptionRule,
  type NotificationSubscriptionState,
} from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';

/**
 * Phase 5.X-per-rule-notification-subscription — user-facing panel for
 * muting/unmuting validation rule notifications. Renders rules grouped
 * by category with a per-category collapse + per-rule toggle. The
 * "Global mute" row at the top suppresses every rule-driven
 * notification regardless of per-rule state.
 *
 * Defaults to subscribed (the backend's opt-IN model): rules with no
 * saved row render with the bell on.
 */
export function NotificationPreferencesPanel(): ReactElement {
  const [state, setState] = useState<NotificationSubscriptionState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, boolean>>({});
  const [globalEdit, setGlobalEdit] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const load = useCallback((): void => {
    setError(null);
    v2.notificationSubscriptions
      .get()
      .then(setState)
      .catch((e: unknown) => setError(formatApiError(e, 'Failed to load preferences')));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const groups: Array<{ category: string; rules: NotificationSubscriptionRule[] }> = useMemo(() => {
    if (!state) return [];
    const map = new Map<string, NotificationSubscriptionRule[]>();
    for (const r of state.rules) {
      const cat = r.category ?? 'Uncategorized';
      const arr = map.get(cat) ?? [];
      arr.push(r);
      map.set(cat, arr);
    }
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([category, rules]) => ({ category, rules }));
  }, [state]);

  const isRuleSubscribed = (r: NotificationSubscriptionRule): boolean =>
    edits[r.ruleId] !== undefined ? edits[r.ruleId]! : r.isSubscribed;

  const isGlobalSubscribed = (): boolean =>
    globalEdit !== null ? globalEdit : (state?.global.isSubscribed ?? true);

  const dirty = Object.keys(edits).length > 0 || globalEdit !== null;

  const handleSave = async (): Promise<void> => {
    if (!state) return;
    const payload: Array<{ ruleId: string | null; isSubscribed: boolean }> = [];
    if (globalEdit !== null) {
      payload.push({ ruleId: null, isSubscribed: globalEdit });
    }
    for (const [ruleId, isSubscribed] of Object.entries(edits)) {
      payload.push({ ruleId, isSubscribed });
    }
    setSaving(true);
    setError(null);
    try {
      await v2.notificationSubscriptions.save(payload);
      setEdits({});
      setGlobalEdit(null);
      load();
    } catch (e) {
      setError(formatApiError(e, 'Failed to save preferences'));
    } finally {
      setSaving(false);
    }
  };

  const muteAll = (): void => {
    if (!state) return;
    const next: Record<string, boolean> = {};
    for (const r of state.rules) next[r.ruleId] = false;
    setEdits(next);
  };

  const restoreAll = (): void => {
    if (!state) return;
    const next: Record<string, boolean> = {};
    for (const r of state.rules) next[r.ruleId] = true;
    setEdits(next);
  };

  return (
    <SettingsCard
      actions={state !== null && (
        <button
          type="button"
          onClick={handleSave}
          disabled={!dirty || saving}
          aria-label="save-preferences"
          style={settingsPrimaryButtonStyle(!dirty || saving)}
        >
          {saving ? 'Saving…' : 'Save preferences'}
        </button>
      )}
      title="Notification preferences"
      ariaLabel="Notification preferences"
      description="Choose which validation rule notifications you receive. New rules are subscribed by default."
    >
      {error && <div role="alert" style={{ color: 'var(--red)', marginBottom: 12 }}>{error}</div>}
      {state === null ? (
        <p>Loading…</p>
      ) : (
        <>
          <div style={settingRowStyle}>
            <div style={settingLabelColumnStyle}>
              <div style={settingLabelStyle}>All notifications</div>
              <div style={settingsDescriptionStyle}>
                When off, all validation rule notifications are muted, regardless of the individual choices below.
              </div>
            </div>
            <SettingsToggle label="global-subscribed" checked={isGlobalSubscribed()} onChange={setGlobalEdit} />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '4px 0 16px' }}>
            <button type="button" onClick={muteAll} style={settingsButtonStyle}>Mute all rules</button>
            <button type="button" onClick={restoreAll} style={settingsButtonStyle}>Restore all rules</button>
          </div>
          {groups.map((g) => {
            const isCollapsed = collapsed[g.category] ?? false;
            return (
              <div key={g.category} style={{ border: '1px solid var(--border)', borderRadius: 6, marginBottom: 12 }} data-testid={`category-${g.category}`}>
                <button
                  type="button"
                  onClick={() => setCollapsed((p) => ({ ...p, [g.category]: !isCollapsed }))}
                  aria-expanded={!isCollapsed}
                  style={{ ...settingsButtonStyle, width: '100%', padding: '12px 16px', background: 'var(--surface2)', border: 'none', textAlign: 'left' }}
                >
                  {isCollapsed ? '▶' : '▼'} {g.category} ({g.rules.length})
                </button>
                {!isCollapsed && (
                  <div style={{ padding: '0 16px' }}>
                    {g.rules.map((r) => (
                      <div key={r.ruleId} style={settingRowStyle}>
                        <div style={settingLabelColumnStyle}>
                          <div style={settingLabelStyle}>{r.name}</div>
                          <div style={settingsDescriptionStyle}>{r.code}</div>
                        </div>
                        <SettingsToggle
                          label={`toggle-${r.code}`}
                          checked={isRuleSubscribed(r)}
                          onChange={(subscribed) => setEdits((p) => ({ ...p, [r.ruleId]: subscribed }))}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {groups.length === 0 && <p style={settingsDescriptionStyle}>No active validation rules to subscribe to.</p>}

        </>
      )}
    </SettingsCard>
  );
}
